import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _typeof from "@babel/runtime/helpers/typeof";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { load, JSON_SCHEMA } from 'js-yaml';
import { severityToPill } from './issue-type';
import { MAX_TRAJECTORY_SUMMARY_BYTES, STEPJSON_READ_CONCURRENCY } from '../config';
import { PackError } from '../pack/errors';
// JSON_SCHEMA keeps unquoted timestamps (kane writes `ended:` unquoted) as
// plain strings instead of Date objects, and `browser_version: '143'` stays a
// string. Unknown keys are always ignored (contract: additive evolution).
var yamlParse = function yamlParse(text) {
  return load(text, {
    schema: JSON_SCHEMA
  });
};
function asRecord(v) {
  return v !== null && _typeof(v) === 'object' && !Array.isArray(v) ? v : null;
}
var asString = function asString(v) {
  return typeof v === 'string' && v !== '' ? v : undefined;
};

/** Loose string coercion for open external_id values ("8827490" or 8827490). */
var asIdString = function asIdString(v) {
  return typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : undefined;
};

/**
 * Parse run.yaml text. Throws typed errors when it isn't a readable evidence
 * manifest. Accepts any 0.x contract (additive minors); rejects other majors
 * loudly rather than mis-reading them (spec: reject-don't-misread).
 */
export function parseRunManifest(yaml) {
  var doc;
  try {
    doc = asRecord(yamlParse(yaml));
  } catch (_unused) {
    throw new PackError('not-a-pack', 'run.yaml is not parseable YAML');
  }
  if (!doc || typeof doc.evidence !== 'string' || typeof doc.run_id !== 'string') {
    throw new PackError('not-a-pack', 'not an evidence run.yaml (missing evidence/run_id)');
  }
  if (!/^0\./.test(doc.evidence)) {
    throw new PackError('unsupported-version', "this viewer reads evidence 0.x; the pack declares \"".concat(doc.evidence, "\""), {
      found: doc.evidence
    });
  }
  return doc;
}

/** Parse tests/<id>/result.yaml text. */
export function parseTestResult(yaml) {
  var doc;
  try {
    doc = asRecord(yamlParse(yaml));
  } catch (_unused2) {
    throw new PackError('not-a-pack', 'result.yaml is not parseable YAML');
  }
  if (!doc || typeof doc.test !== 'string') {
    throw new PackError('not-a-pack', 'not an evidence result.yaml (missing test)');
  }
  return doc;
}
var TEST_STATUSES = ['passed', 'failed', 'broken', 'skipped'];
export function normalizeTestStatus(s) {
  return TEST_STATUSES.includes(s) ? s : 'unknown';
}
function normalizeLifecycle(s) {
  return s === 'running' || s === 'finalized' || s === 'aborted' ? s : 'unknown';
}
function parseInstant(s) {
  if (typeof s !== 'string') return null;
  var t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

/**
 * Derive the run-level chip. `status` is a lifecycle, so a finalized run's
 * displayed verdict comes from the authored totals — broken is never
 * collapsed into failed.
 */
function deriveChip(lifecycle, totals) {
  if (lifecycle === 'running') return {
    kind: 'lifecycle',
    lifecycle: lifecycle
  };
  if (lifecycle === 'aborted') return {
    kind: 'lifecycle',
    lifecycle: lifecycle
  };
  if (totals.failed > 0) return {
    kind: 'verdict',
    verdict: 'failed'
  };
  if (totals.broken > 0) return {
    kind: 'verdict',
    verdict: 'broken'
  };
  if (totals.passed > 0) return {
    kind: 'verdict',
    verdict: 'passed'
  };
  return {
    kind: 'verdict',
    verdict: 'skipped'
  };
}
export function toRunVM(m) {
  var _m$totals$tests, _m$totals, _m$totals$passed, _m$totals2, _m$totals$failed, _m$totals3, _m$totals$broken, _m$totals4, _m$totals$skipped, _m$totals5, _m$environment, _asString, _asString2, _asString3, _asString4;
  var totals = {
    tests: (_m$totals$tests = (_m$totals = m.totals) === null || _m$totals === void 0 ? void 0 : _m$totals.tests) !== null && _m$totals$tests !== void 0 ? _m$totals$tests : 0,
    passed: (_m$totals$passed = (_m$totals2 = m.totals) === null || _m$totals2 === void 0 ? void 0 : _m$totals2.passed) !== null && _m$totals$passed !== void 0 ? _m$totals$passed : 0,
    failed: (_m$totals$failed = (_m$totals3 = m.totals) === null || _m$totals3 === void 0 ? void 0 : _m$totals3.failed) !== null && _m$totals$failed !== void 0 ? _m$totals$failed : 0,
    broken: (_m$totals$broken = (_m$totals4 = m.totals) === null || _m$totals4 === void 0 ? void 0 : _m$totals4.broken) !== null && _m$totals$broken !== void 0 ? _m$totals$broken : 0,
    skipped: (_m$totals$skipped = (_m$totals5 = m.totals) === null || _m$totals5 === void 0 ? void 0 : _m$totals5.skipped) !== null && _m$totals$skipped !== void 0 ? _m$totals$skipped : 0
  };
  var lifecycle = normalizeLifecycle(m.status);
  var started = parseInstant(m.started);
  var ended = parseInstant(m.ended);
  var environment = (_m$environment = m.environment) !== null && _m$environment !== void 0 ? _m$environment : {};
  var producer = asRecord(environment.producer);
  return {
    runId: m.run_id,
    contractVersion: m.evidence,
    title: (_asString = asString(m.title)) !== null && _asString !== void 0 ? _asString : m.run_id,
    lifecycle: lifecycle,
    chip: deriveChip(lifecycle, totals),
    totals: totals,
    passRatePct: totals.tests > 0 ? Math.round(totals.passed / totals.tests * 100) : null,
    durationMs: started !== null && ended !== null && ended >= started ? ended - started : null,
    producerName: producer ? (_asString2 = asString(producer.name)) !== null && _asString2 !== void 0 ? _asString2 : null : null,
    startedAt: (_asString3 = asString(m.started)) !== null && _asString3 !== void 0 ? _asString3 : null,
    endedAt: (_asString4 = asString(m.ended)) !== null && _asString4 !== void 0 ? _asString4 : null,
    flakyCount: null,
    environment: environment
  };
}
var asFiniteNumber = function asFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
};
export function toSkinData(v) {
  var rec = asRecord(v);
  if (!rec) return undefined;
  var data = {
    top: asFiniteNumber(rec.top),
    right: asFiniteNumber(rec.right),
    bottom: asFiniteNumber(rec.bottom),
    left: asFiniteNumber(rec.left),
    width: asFiniteNumber(rec.width),
    height: asFiniteNumber(rec.height),
    radius: asFiniteNumber(rec.radius)
  };
  // Geometry is unusable without the image's native size.
  return data.width && data.height ? data : undefined;
}
function toRowEnv(env) {
  if (!env) return undefined;
  var row = {
    os: asString(env.os),
    osVersion: asIdString(env.os_version),
    browser: asString(env.browser),
    browserVersion: asIdString(env.browser_version),
    resolution: asString(env.resolution),
    platform: asString(env.platform),
    skinUrl: asString(env.skin_url),
    skinData: toSkinData(env.skin_data),
    viewport: asString(env.viewport),
    device: asString(env.device),
    deviceSlug: asString(env.device_slug)
  };
  return Object.values(row).some(function (v) {
    return v !== undefined;
  }) ? row : undefined;
}

/**
 * Project a parsed result.yaml onto the row the list renders. Status is the
 * AUTHORED value only (never derived from steps); duration falls back to the
 * sum of the steps' authored durations (steps may be empty or partial).
 */
export function toTestRow(id, r) {
  var _asRecord, _asRecord2;
  var externalId = (_asRecord = asRecord(r.external_id)) !== null && _asRecord !== void 0 ? _asRecord : undefined;
  var steps = Array.isArray(r.steps) ? r.steps : [];
  var stepDurations = steps.map(function (s) {
    return typeof (s === null || s === void 0 ? void 0 : s.duration_ms) === 'number' ? s.duration_ms : null;
  }).filter(function (d) {
    return d !== null;
  });
  var durationMs = typeof r.duration_ms === 'number' ? r.duration_ms : stepDurations.length > 0 ? stepDurations.reduce(function (a, b) {
    return a + b;
  }, 0) : undefined;
  return {
    id: id,
    state: 'loaded',
    status: normalizeTestStatus(r.status),
    title: externalId && asString(externalId.session_name) || id,
    testcaseId: externalId ? asIdString(externalId.testcase_id) : undefined,
    tcId: externalId ? asIdString(externalId.tc_id) : undefined,
    projectId: externalId ? asIdString(externalId.project_id) : undefined,
    datasetId: externalId ? asIdString(externalId.dataset_id) : undefined,
    externalId: externalId,
    env: toRowEnv((_asRecord2 = asRecord(r.environment)) !== null && _asRecord2 !== void 0 ? _asRecord2 : undefined),
    durationMs: durationMs,
    stepCount: steps.length,
    flaky: r.flaky === true,
    attempts: Array.isArray(r.attempts) ? r.attempts.map(function (a) {
      return {
        status: normalizeTestStatus(a === null || a === void 0 ? void 0 : a.status),
        durationMs: typeof (a === null || a === void 0 ? void 0 : a.duration_ms) === 'number' ? a.duration_ms : null
      };
    }) : undefined
  };
}
export function pendingRow(id) {
  return {
    id: id,
    state: 'pending',
    status: 'unknown',
    title: id
  };
}
export function errorRow(id, message) {
  return {
    id: id,
    state: 'error',
    status: 'unknown',
    title: id,
    errorMessage: message
  };
}

/**
 * List a test's steps in ordinal order, each hydrated from its step.json and
 * carrying its screenshot's pack-root-relative path when one exists.
 * Permissive by design: a folder with a broken/missing step.json still shows
 * up (bare ordinal), and a missing screenshot is simply omitted. Detail-view
 * concern — never called during list hydration.
 */
export function listSteps(_x, _x2) {
  return _listSteps.apply(this, arguments);
}

/**
 * Screenshot / annotated paths for a test's steps from the central directory
 * ALONE — no step.json reads, no cursor enrichment. Powers the first detail
 * paint so the selected screenshot can fetch immediately; listSteps() then
 * refines each step from its step.json. listDir hits the in-memory ZIP central
 * directory (zero network), so this pass is effectively free.
 */
function _listSteps() {
  _listSteps = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(pack, testId) {
    var stepsDir, folders, infos;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          stepsDir = "tests/".concat(testId, "/steps");
          _context2.next = 1;
          return pack.listDir(stepsDir);
        case 1:
          folders = _context2.sent.filter(function (e) {
            return e.isDir;
          });
          _context2.next = 2;
          return mapPool(folders, STEPJSON_READ_CONCURRENCY, /*#__PURE__*/function () {
            var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(folder) {
              var ordinal, base, children, shot, annotated, raw, j, _iterator2, _step2, k, coords, rect;
              return _regeneratorRuntime.wrap(function (_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    ordinal = parseInt(folder.name, 10);
                    base = {
                      dirName: folder.name,
                      ordinal: Number.isNaN(ordinal) ? 0 : ordinal
                    };
                    _context.next = 1;
                    return pack.listDir("".concat(stepsDir, "/").concat(folder.name));
                  case 1:
                    children = _context.sent;
                    shot = children.find(function (c) {
                      return !c.isDir && c.name.startsWith('screenshot.');
                    });
                    if (shot) base.screenshotPath = "".concat(stepsDir, "/").concat(folder.name, "/").concat(shot.name);
                    annotated = children.find(function (c) {
                      return !c.isDir && c.name.startsWith('annotated.');
                    });
                    if (annotated) {
                      base.annotatedPath = "".concat(stepsDir, "/").concat(folder.name, "/").concat(annotated.name);
                    }
                    _context.next = 2;
                    return pack.readText("".concat(stepsDir, "/").concat(folder.name, "/step.json"));
                  case 2:
                    raw = _context.sent;
                    if (raw) {
                      try {
                        j = asRecord(JSON.parse(raw));
                        if (j) {
                          _iterator2 = _createForOfIteratorHelper(['id', 'kind', 'status', 'summary', 'url', 'surface']);
                          try {
                            for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
                              k = _step2.value;
                              if (typeof j[k] === 'string') base[k] = j[k];
                            }
                          } catch (err) {
                            _iterator2.e(err);
                          } finally {
                            _iterator2.f();
                          }
                          if (typeof j.duration_ms === 'number') {
                            base.duration_ms = j.duration_ms;
                          }
                          if (typeof j.action_id === 'string') base.actionId = j.action_id;
                          coords = asRecord(j.coordinates);
                          if (coords && typeof coords.x === 'number' && typeof coords.y === 'number') {
                            base.coordinates = {
                              x: coords.x,
                              y: coords.y
                            };
                          }
                          rect = asRecord(j.element_rect);
                          if (rect && typeof rect.x === 'number' && typeof rect.y === 'number' && typeof rect.width === 'number' && typeof rect.height === 'number') {
                            base.elementRect = {
                              x: rect.x,
                              y: rect.y,
                              width: rect.width,
                              height: rect.height
                            };
                          }
                        }
                      } catch (_unused7) {
                        // Unparseable step.json — keep the bare folder info.
                      }
                    }
                    return _context.abrupt("return", base);
                  case 3:
                  case "end":
                    return _context.stop();
                }
              }, _callee);
            }));
            return function (_x17) {
              return _ref2.apply(this, arguments);
            };
          }());
        case 2:
          infos = _context2.sent;
          return _context2.abrupt("return", infos.sort(function (a, b) {
            return a.ordinal - b.ordinal;
          }));
        case 3:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _listSteps.apply(this, arguments);
}
export function listStepShots(_x3, _x4) {
  return _listStepShots.apply(this, arguments);
}

/** tests/<id>/auteur/dataset.json → DatasetInfo; null when unreadable. */
function _listStepShots() {
  _listStepShots = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4(pack, testId) {
    var stepsDir, folders, infos;
    return _regeneratorRuntime.wrap(function (_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          stepsDir = "tests/".concat(testId, "/steps");
          _context4.next = 1;
          return pack.listDir(stepsDir);
        case 1:
          folders = _context4.sent.filter(function (e) {
            return e.isDir;
          });
          _context4.next = 2;
          return Promise.all(folders.map(/*#__PURE__*/function () {
            var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(folder) {
              var ordinal, base, children, shot, annotated;
              return _regeneratorRuntime.wrap(function (_context3) {
                while (1) switch (_context3.prev = _context3.next) {
                  case 0:
                    ordinal = parseInt(folder.name, 10);
                    base = {
                      dirName: folder.name,
                      ordinal: Number.isNaN(ordinal) ? 0 : ordinal
                    };
                    _context3.next = 1;
                    return pack.listDir("".concat(stepsDir, "/").concat(folder.name));
                  case 1:
                    children = _context3.sent;
                    shot = children.find(function (c) {
                      return !c.isDir && c.name.startsWith('screenshot.');
                    });
                    if (shot) base.screenshotPath = "".concat(stepsDir, "/").concat(folder.name, "/").concat(shot.name);
                    annotated = children.find(function (c) {
                      return !c.isDir && c.name.startsWith('annotated.');
                    });
                    if (annotated) {
                      base.annotatedPath = "".concat(stepsDir, "/").concat(folder.name, "/").concat(annotated.name);
                    }
                    return _context3.abrupt("return", base);
                  case 2:
                  case "end":
                    return _context3.stop();
                }
              }, _callee3);
            }));
            return function (_x18) {
              return _ref3.apply(this, arguments);
            };
          }()));
        case 2:
          infos = _context4.sent;
          return _context4.abrupt("return", infos.sort(function (a, b) {
            return a.ordinal - b.ordinal;
          }));
        case 3:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
  return _listStepShots.apply(this, arguments);
}
export function parseDatasetInfo(text) {
  var doc = null;
  try {
    doc = asRecord(JSON.parse(text));
  } catch (_unused3) {
    return null;
  }
  if (!doc) return null;
  var str = function str(v) {
    return typeof v === 'string' && v !== '' ? v : null;
  };
  return {
    id: str(doc.dataset_id),
    name: str(doc.dataset_name),
    versionId: str(doc.dataset_version_id),
    rowId: str(doc.row_id),
    rowIndex: typeof doc.row_index === 'number' && Number.isFinite(doc.row_index) ? doc.row_index : null,
    columns: Array.isArray(doc.columns) ? doc.columns.filter(function (c) {
      return typeof c === 'string';
    }) : [],
    values: asRecord(doc.values)
  };
}

/** A pack's dataset.json for one test; null when absent or unreadable —
 * never throws for content problems (only aborts propagate). */
export function readDatasetInfo(_x5, _x6, _x7) {
  return _readDatasetInfo.apply(this, arguments);
}
function _readDatasetInfo() {
  _readDatasetInfo = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee5(pack, testId, signal) {
    var rel, text;
    return _regeneratorRuntime.wrap(function (_context5) {
      while (1) switch (_context5.prev = _context5.next) {
        case 0:
          rel = "tests/".concat(testId, "/auteur/dataset.json");
          _context5.next = 1;
          return pack.exists(rel);
        case 1:
          if (_context5.sent) {
            _context5.next = 2;
            break;
          }
          return _context5.abrupt("return", null);
        case 2:
          _context5.next = 3;
          return pack.readText(rel, {
            signal: signal
          });
        case 3:
          text = _context5.sent;
          return _context5.abrupt("return", text === null ? null : parseDatasetInfo(text));
        case 4:
        case "end":
          return _context5.stop();
      }
    }, _callee5);
  }));
  return _readDatasetInfo.apply(this, arguments);
}
var TRAJECTORY_SUMMARY = /^(\d+)-run_summary\.json$/;

/**
 * kane's per-objective agent summaries (tests/<id>/v16-trajectory/
 * <index>-run_summary.json): final_status + reason for each objective. The
 * listing is central-directory knowledge (zero network); each summary is a
 * small size-guarded read. Unreadable or malformed summaries are skipped.
 * Detail-view concern — never called during list hydration.
 */
export function listObjectiveOutcomes(_x8, _x9, _x0) {
  return _listObjectiveOutcomes.apply(this, arguments);
}

/** Run `fn` over `items` with at most `limit` in flight — a step listing on a
 * 500-step test must not open hundreds of concurrent ranged reads. */
function _listObjectiveOutcomes() {
  _listObjectiveOutcomes = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee7(pack, testId, signal) {
    var dir, summaries, outcomes;
    return _regeneratorRuntime.wrap(function (_context7) {
      while (1) switch (_context7.prev = _context7.next) {
        case 0:
          dir = "tests/".concat(testId, "/v16-trajectory");
          _context7.next = 1;
          return pack.listDir(dir);
        case 1:
          summaries = _context7.sent.filter(function (e) {
            return !e.isDir && TRAJECTORY_SUMMARY.test(e.name);
          });
          _context7.next = 2;
          return mapPool(summaries, STEPJSON_READ_CONCURRENCY, /*#__PURE__*/function () {
            var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee6(entry) {
              var _summary, _summary2;
              var rel, size, raw, summary, _t;
              return _regeneratorRuntime.wrap(function (_context6) {
                while (1) switch (_context6.prev = _context6.next) {
                  case 0:
                    rel = "".concat(dir, "/").concat(entry.name);
                    size = pack.entrySize(rel);
                    if (!(size !== null && size > MAX_TRAJECTORY_SUMMARY_BYTES)) {
                      _context6.next = 1;
                      break;
                    }
                    return _context6.abrupt("return", null);
                  case 1:
                    _context6.next = 2;
                    return pack.readText(rel, {
                      signal: signal
                    });
                  case 2:
                    raw = _context6.sent;
                    if (raw) {
                      _context6.next = 3;
                      break;
                    }
                    return _context6.abrupt("return", null);
                  case 3:
                    summary = null;
                    _context6.prev = 4;
                    summary = asRecord(JSON.parse(raw));
                    _context6.next = 6;
                    break;
                  case 5:
                    _context6.prev = 5;
                    _t = _context6["catch"](4);
                    return _context6.abrupt("return", null);
                  case 6:
                    return _context6.abrupt("return", {
                      index: parseInt(entry.name, 10),
                      finalStatus: typeof ((_summary = summary) === null || _summary === void 0 ? void 0 : _summary.final_status) === 'string' ? summary.final_status : null,
                      reason: typeof ((_summary2 = summary) === null || _summary2 === void 0 ? void 0 : _summary2.reason) === 'string' ? summary.reason : null
                    });
                  case 7:
                  case "end":
                    return _context6.stop();
                }
              }, _callee6, null, [[4, 5]]);
            }));
            return function (_x19) {
              return _ref4.apply(this, arguments);
            };
          }());
        case 2:
          outcomes = _context7.sent;
          return _context7.abrupt("return", outcomes.filter(function (o) {
            return o !== null;
          }).sort(function (a, b) {
            return a.index - b.index;
          }));
        case 3:
        case "end":
          return _context7.stop();
      }
    }, _callee7);
  }));
  return _listObjectiveOutcomes.apply(this, arguments);
}
function mapPool(_x1, _x10, _x11) {
  return _mapPool.apply(this, arguments);
}
function _mapPool() {
  _mapPool = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee9(items, limit, fn) {
    var out, next, worker;
    return _regeneratorRuntime.wrap(function (_context9) {
      while (1) switch (_context9.prev = _context9.next) {
        case 0:
          out = new Array(items.length);
          next = 0;
          worker = /*#__PURE__*/function () {
            var _ref5 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
              var i;
              return _regeneratorRuntime.wrap(function (_context8) {
                while (1) switch (_context8.prev = _context8.next) {
                  case 0:
                    if (!(next < items.length)) {
                      _context8.next = 2;
                      break;
                    }
                    i = next;
                    next += 1;
                    _context8.next = 1;
                    return fn(items[i]);
                  case 1:
                    out[i] = _context8.sent;
                    _context8.next = 0;
                    break;
                  case 2:
                  case "end":
                    return _context8.stop();
                }
              }, _callee8);
            }));
            return function worker() {
              return _ref5.apply(this, arguments);
            };
          }();
          _context9.next = 1;
          return Promise.all(Array.from({
            length: Math.min(limit, items.length)
          }, worker));
        case 1:
          return _context9.abrupt("return", out);
        case 2:
        case "end":
          return _context9.stop();
      }
    }, _callee9);
  }));
  return _mapPool.apply(this, arguments);
}
function formatFromExtension(file) {
  if (file.endsWith('.ndjson')) return 'ndjson';
  // .har before .json — HAR is json but the network dock keys on 'har'
  // (retry mirrors ship no logs/meta.yaml, so this fallback is their
  // only format source).
  if (file.endsWith('.har')) return 'har';
  if (file.endsWith('.json')) return 'json';
  return 'log';
}

/**
 * The test's log index from logs/meta.yaml; falls back to listing logs/
 * when the index is absent (format guessed from the extension).
 */
export function listLogs(_x12, _x13) {
  return _listLogs.apply(this, arguments);
}

/** One action record's cursor facts from logs/*-actions.ndjson.
 * Mobile packs author click coordinates ONLY here (web packs put them in
 * step.json directly) — visual_data carries {x, y, bbox} in viewport px. */
function _listLogs() {
  _listLogs = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee0(pack, testId) {
    var logsDir, metaText, doc, logs, refs, _iterator3, _step3, l, r;
    return _regeneratorRuntime.wrap(function (_context0) {
      while (1) switch (_context0.prev = _context0.next) {
        case 0:
          logsDir = "tests/".concat(testId, "/logs");
          _context0.next = 1;
          return pack.readText("".concat(logsDir, "/meta.yaml"));
        case 1:
          metaText = _context0.sent;
          if (!metaText) {
            _context0.next = 2;
            break;
          }
          doc = null;
          try {
            doc = asRecord(yamlParse(metaText));
          } catch (_unused9) {
            // fall through to directory listing
          }
          if (!doc) {
            _context0.next = 2;
            break;
          }
          logs = Array.isArray(doc.logs) ? doc.logs : [];
          refs = [];
          _iterator3 = _createForOfIteratorHelper(logs);
          try {
            for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
              l = _step3.value;
              r = asRecord(l);
              if (r && typeof r.file === 'string') {
                refs.push({
                  name: typeof r.name === 'string' ? r.name : r.file,
                  file: r.file,
                  format: typeof r.format === 'string' ? r.format : formatFromExtension(r.file)
                });
              }
            }
          } catch (err) {
            _iterator3.e(err);
          } finally {
            _iterator3.f();
          }
          return _context0.abrupt("return", refs);
        case 2:
          _context0.next = 3;
          return pack.listDir(logsDir);
        case 3:
          return _context0.abrupt("return", _context0.sent.filter(function (e) {
            return !e.isDir;
          }).map(function (e) {
            return {
              name: e.name.replace(/\.[^.]+$/, ''),
              file: e.name,
              format: formatFromExtension(e.name)
            };
          }));
        case 4:
        case "end":
          return _context0.stop();
      }
    }, _callee0);
  }));
  return _listLogs.apply(this, arguments);
}
export function parseActionCursors(text) {
  var out = [];
  var _iterator = _createForOfIteratorHelper(text.split('\n')),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var line = _step.value;
      var trimmed = line.trim();
      if (trimmed === '') continue;
      try {
        var _point;
        var rec = asRecord(JSON.parse(trimmed));
        var actionId = rec ? asString(rec.action_id) : undefined;
        if (!rec || !actionId) continue;
        var visual = asRecord(rec.visual_data);
        var coords = asRecord(rec.coordinates);
        var point = function point(v) {
          return v && typeof v.x === 'number' && typeof v.y === 'number' ? {
            x: v.x,
            y: v.y
          } : null;
        };
        var bbox = visual ? asRecord(visual.bbox) : null;
        var rect = bbox && typeof bbox.x === 'number' && typeof bbox.y === 'number' && typeof bbox.width === 'number' && typeof bbox.height === 'number' ? {
          x: bbox.x,
          y: bbox.y,
          width: bbox.width,
          height: bbox.height
        } : null;
        var cursor = (_point = point(visual)) !== null && _point !== void 0 ? _point : point(coords);
        var viewport = visual && typeof visual.viewport_width === 'number' && typeof visual.viewport_height === 'number' && visual.viewport_width > 0 && visual.viewport_height > 0 ? {
          w: visual.viewport_width,
          h: visual.viewport_height
        } : null;
        if (cursor || rect) out.push({
          actionId: actionId,
          cursor: cursor,
          rect: rect,
          viewport: viewport
        });
      } catch (_unused4) {
        // One bad line never poisons the rest of the log.
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return out;
}

/** Parse the root failure.yaml index. Returns null (never throws) on any
 * shape problem — attention data must never break the run view. */
export function parseFailureIndex(yaml) {
  try {
    var doc = asRecord(yamlParse(yaml));
    if (!doc || !Array.isArray(doc.failures)) return null;
    return doc;
  } catch (_unused5) {
    return null;
  }
}

/** One `{ref, step}` log pointer from the mechanical core. */
function toLogRef(v) {
  var _ref;
  var rec = asRecord(v);
  var ref = rec ? asString(rec.ref) : undefined;
  if (!ref) return undefined;
  return {
    ref: ref,
    step: (_ref = rec && asString(rec.step)) !== null && _ref !== void 0 ? _ref : null
  };
}

/**
 * The full per-step failure.yaml record (mechanical core + triage block).
 * `step` is the page-state anchor the verdict named as culprit — it may be
 * mechanically "passed" in result.yaml (the status_disagrees advisory).
 */
export function parseFailureDetail(yaml) {
  try {
    var doc = asRecord(yamlParse(yaml));
    if (!doc) return {};
    var error = asRecord(doc.error);
    var pageState = asRecord(doc.page_state);
    var triage = asRecord(doc.triage);
    var by = triage ? asRecord(triage.by) : null;
    var rca = triage ? asRecord(triage.rca) : null;
    var fix = triage ? asRecord(triage.suggested_fix) : null;
    var confidence = rca === null || rca === void 0 ? void 0 : rca.confidence;
    return {
      message: error ? asString(error.message) : undefined,
      rootCause: rca ? asString(rca.root_cause) : undefined,
      triageStatus: triage ? asString(triage.status) : undefined,
      suggestedFix: fix ? asString(fix.summary) : undefined,
      step: asString(doc.step),
      title: asString(doc.title),
      category: rca ? asString(rca.category) : undefined,
      confidence: typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : undefined,
      pageUrl: pageState ? asString(pageState.url) : undefined,
      screenshot: pageState ? asString(pageState.screenshot) : undefined,
      consoleRef: toLogRef(doc.console_at_failure),
      networkRef: toLogRef(doc.network_at_failure),
      severity: triage ? asString(triage.severity) : undefined,
      triagedById: by ? asString(by.id) : undefined,
      triagedAt: by ? asString(by.at) : undefined
    };
  } catch (_unused6) {
    return {};
  }
}
var normalizeTriage = function normalizeTriage(s) {
  return s === 'triaged' || s === 'untriaged' ? s : 'unknown';
};

/**
 * Assemble the "Need your attention" view-model: the root failure.yaml
 * index plus each referenced per-step detail (a handful of ~1 KB ranged
 * reads). Returns null when the pack carries no failure data — the widget
 * hides. Per-item failures degrade to id-based titles, never to an error.
 */
export function readAttention(_x14, _x15, _x16) {
  return _readAttention.apply(this, arguments);
}
function _readAttention() {
  _readAttention = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee10(pack, maxItems, onIndex) {
    var _index$failures;
    var indexText, index, refs, toItem, toVM, items;
    return _regeneratorRuntime.wrap(function (_context10) {
      while (1) switch (_context10.prev = _context10.next) {
        case 0:
          _context10.next = 1;
          return pack.readText('failure.yaml');
        case 1:
          indexText = _context10.sent;
          if (!(indexText === null)) {
            _context10.next = 2;
            break;
          }
          return _context10.abrupt("return", null);
        case 2:
          index = parseFailureIndex(indexText);
          if (index) {
            _context10.next = 3;
            break;
          }
          return _context10.abrupt("return", null);
        case 3:
          refs = ((_index$failures = index.failures) !== null && _index$failures !== void 0 ? _index$failures : []).slice(0, maxItems);
          toItem = function toItem(ref, detail) {
            var _asString5, _ref6, _ref7, _detail$title, _detail$severity, _detail$triageStatus, _detail$step;
            var testId = (_asString5 = asString(ref.test)) !== null && _asString5 !== void 0 ? _asString5 : 'unknown-test';
            var fallbackTitle = ref.ordinal !== undefined ? "".concat(testId, " \u2014 step ").concat(ref.ordinal, " failed") : "".concat(testId, " \u2014 failure");
            return {
              testId: testId,
              ordinal: typeof ref.ordinal === 'number' ? ref.ordinal : undefined,
              // Bug name when authored (record, then the seal-time index lift),
              // else the raw error, else the id-based fallback.
              title: (_ref6 = (_ref7 = (_detail$title = detail.title) !== null && _detail$title !== void 0 ? _detail$title : asString(ref.title)) !== null && _ref7 !== void 0 ? _ref7 : detail.message) !== null && _ref6 !== void 0 ? _ref6 : fallbackTitle,
              severity: severityToPill((_detail$severity = detail.severity) !== null && _detail$severity !== void 0 ? _detail$severity : asString(ref.severity)),
              triageStatus: normalizeTriage((_detail$triageStatus = detail.triageStatus) !== null && _detail$triageStatus !== void 0 ? _detail$triageStatus : asString(ref.triage_status)),
              rootCause: detail.rootCause,
              stepId: (_detail$step = detail.step) !== null && _detail$step !== void 0 ? _detail$step : asString(ref.step),
              status: ref.status === 'broken' ? 'broken' : 'failed',
              path: asString(ref.path),
              category: detail.category,
              confidence: detail.confidence,
              triagedAt: detail.triagedAt
            };
          };
          toVM = function toVM(items) {
            var _index$totals$failure, _index$totals, _index$totals$triaged, _index$totals2, _index$totals$untriag, _index$totals3;
            return {
              totals: {
                failures: (_index$totals$failure = (_index$totals = index.totals) === null || _index$totals === void 0 ? void 0 : _index$totals.failures) !== null && _index$totals$failure !== void 0 ? _index$totals$failure : items.length,
                triaged: (_index$totals$triaged = (_index$totals2 = index.totals) === null || _index$totals2 === void 0 ? void 0 : _index$totals2.triaged) !== null && _index$totals$triaged !== void 0 ? _index$totals$triaged : 0,
                untriaged: (_index$totals$untriag = (_index$totals3 = index.totals) === null || _index$totals3 === void 0 ? void 0 : _index$totals3.untriaged) !== null && _index$totals$untriag !== void 0 ? _index$totals$untriag : 0
              },
              items: items
            };
          };
          if (!(refs.length === 0)) {
            _context10.next = 4;
            break;
          }
          return _context10.abrupt("return", null);
        case 4:
          // Two-phase hydration: the index alone (ONE read) already carries rows
          // (title, step, triage status) — publish them immediately so the report
          // block and Issues tab render without waiting for the per-step record
          // reads, which then enrich in place (type chip, confidence, root cause).
          onIndex === null || onIndex === void 0 || onIndex(toVM(refs.map(function (ref) {
            return toItem(ref, {});
          })));
          _context10.next = 5;
          return Promise.all(refs.map(/*#__PURE__*/function () {
            var _ref8 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee1(ref) {
              var detail, text, _t2;
              return _regeneratorRuntime.wrap(function (_context1) {
                while (1) switch (_context1.prev = _context1.next) {
                  case 0:
                    detail = {};
                    if (!asString(ref.path)) {
                      _context1.next = 4;
                      break;
                    }
                    _context1.prev = 1;
                    _context1.next = 2;
                    return pack.readText(ref.path);
                  case 2:
                    text = _context1.sent;
                    if (text !== null) detail = parseFailureDetail(text);
                    _context1.next = 4;
                    break;
                  case 3:
                    _context1.prev = 3;
                    _t2 = _context1["catch"](1);
                  case 4:
                    return _context1.abrupt("return", toItem(ref, detail));
                  case 5:
                  case "end":
                    return _context1.stop();
                }
              }, _callee1, null, [[1, 3]]);
            }));
            return function (_x20) {
              return _ref8.apply(this, arguments);
            };
          }()));
        case 5:
          items = _context10.sent;
          return _context10.abrupt("return", toVM(items));
        case 6:
        case "end":
          return _context10.stop();
      }
    }, _callee10);
  }));
  return _readAttention.apply(this, arguments);
}